import type {
  PlanogramAssetProvider,
  PlanogramProductAsset,
  PlanogramProductAssetRequest,
} from "./types";

export type PlanogramAssetCacheEntry =
  | { readonly status: "pending"; readonly promise: Promise<PlanogramProductAsset | null> }
  | { readonly asset: PlanogramProductAsset | null; readonly status: "resolved" };

export interface PlanogramAssetCache {
  get: (
    provider: PlanogramAssetProvider,
    request: PlanogramProductAssetRequest,
  ) => PlanogramAssetCacheEntry | undefined;
  load: (
    provider: PlanogramAssetProvider,
    request: PlanogramProductAssetRequest,
  ) => Promise<PlanogramProductAsset | null>;
}

const getAssetKey = ({ productCode, productId }: PlanogramProductAssetRequest) =>
  productCode.trim().length > 0 ? productCode : productId;

/**
 * Keeps one request/result per provider and product identity. The cache is deliberately
 * provider-scoped: a test or caller can replace its provider without receiving another
 * provider's image, while repeated shelf and pegboard entities share the same request.
 */
const providerCaches = new WeakMap<PlanogramAssetProvider, PlanogramAssetCache>();
const DEFAULT_ASSET_CACHE_LIMIT = 256;

export const createPlanogramAssetCache = (
  maxEntries = DEFAULT_ASSET_CACHE_LIMIT,
): PlanogramAssetCache => {
  const entryLimit = Number.isFinite(maxEntries)
    ? Math.max(1, Math.floor(maxEntries))
    : DEFAULT_ASSET_CACHE_LIMIT;
  const providerEntries = new WeakMap<
    PlanogramAssetProvider,
    Map<string, PlanogramAssetCacheEntry>
  >();

  const touch = (entries: Map<string, PlanogramAssetCacheEntry>, key: string) => {
    const entry = entries.get(key);
    if (!entry) return undefined;
    entries.delete(key);
    entries.set(key, entry);
    return entry;
  };

  const prune = (entries: Map<string, PlanogramAssetCacheEntry>) => {
    while (entries.size > entryLimit) {
      for (const oldestKey of entries.keys()) {
        entries.delete(oldestKey);
        break;
      }
    }
  };

  const get = (provider: PlanogramAssetProvider, request: PlanogramProductAssetRequest) => {
    const entries = providerEntries.get(provider);
    return entries ? touch(entries, getAssetKey(request)) : undefined;
  };

  const load = async (
    provider: PlanogramAssetProvider,
    request: PlanogramProductAssetRequest,
  ): Promise<PlanogramProductAsset | null> => {
    const entries = providerEntries.get(provider) ?? new Map<string, PlanogramAssetCacheEntry>();
    providerEntries.set(provider, entries);
    const key = getAssetKey(request);
    const existing = touch(entries, key);
    if (existing?.status === "resolved") return existing.asset;
    if (existing?.status === "pending") return existing.promise;

    // The provider is invoked synchronously so the request is already in
    // flight when the caller's effect returns (tests and rasterization hosts
    // observe the request without waiting for an extra microtask).
    let loadResult: ReturnType<PlanogramAssetProvider["loadProductImage"]>;
    try {
      loadResult = provider.loadProductImage(request);
    } catch {
      loadResult = null;
    }
    const promise = Promise.resolve(loadResult)
      .catch(() => null)
      .then((asset) => {
        const current = entries.get(key);
        if (current?.status === "pending" && current.promise === promise) {
          entries.set(key, { asset, status: "resolved" });
          touch(entries, key);
        }
        return asset;
      });
    entries.set(key, { promise, status: "pending" });
    prune(entries);
    return promise;
  };

  return { get, load };
};

export const getSharedPlanogramAssetCache = (
  provider: PlanogramAssetProvider,
): PlanogramAssetCache => {
  const existing = providerCaches.get(provider);
  if (existing) return existing;
  const cache = createPlanogramAssetCache();
  providerCaches.set(provider, cache);
  return cache;
};
